"use client"

import { useState, useEffect, useCallback } from "react"
import { MessageCircle } from "lucide-react"
import { AnimatePresence, motion } from "framer-motion"
import { useChatbotContext } from "./ChatbotProvider"
import ChatbotPanel from "./ChatbotPanel"
import { cn } from "@/lib/utils"

export default function ChatbotWidget() {
  const [isOpen, setIsOpen] = useState(false)
  const [hasInteracted, setHasInteracted] = useState(false)
  const {
    messages,
    isStreaming,
    error,
    sendMessage,
    clearChat,
    stopStreaming,
    executeAction,
    addBotMessage,
  } = useChatbotContext()

  const handleOpen = useCallback(() => {
    setIsOpen(true)
    setHasInteracted(true)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false)
    }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [isOpen])

  return (
    <>
      {/* Floating bubble */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            onClick={handleOpen}
            className={cn(
              "fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center",
              "rounded-full bg-primary text-primary-foreground shadow-lg",
              "transition-transform hover:scale-105 active:scale-95",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            )}
            aria-label="Open support chat"
          >
            <MessageCircle className="h-6 w-6" />
            {/* Notification dot on first visit */}
            {!hasInteracted && (
              <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex h-3.5 w-3.5 rounded-full bg-green-500" />
              </span>
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Expanded panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className={cn(
              "fixed z-50",
              // Desktop
              "bottom-6 right-6 h-[520px] w-[380px]",
              // Mobile
              "max-sm:bottom-0 max-sm:right-0 max-sm:left-0 max-sm:top-20",
              "max-sm:h-auto max-sm:w-full max-sm:rounded-none"
            )}
          >
            <ChatbotPanel
              variant="widget"
              messages={messages}
              isStreaming={isStreaming}
              error={error}
              onSend={sendMessage}
              onStop={stopStreaming}
              onClear={clearChat}
              onAction={executeAction}
              onAddBotMessage={addBotMessage}
              onClose={handleClose}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
